import React, { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { map, get } from 'lodash';
import { RootState } from 'modules';
import { getCaseSeason, getCaseQNACompany, getQNACategory, getQNASubcategory } from 'libs/api/case';
import { verifiableCompanyName } from 'libs/api/user';
import { TSeason, TCompany } from 'modules/case/types';
import { TModalProps } from 'modules/types';
import { CPositionType, CCountryType, CAvailableTimeTypes } from 'constants/user.const';
import {
  ECountryType,
  EPositionType,
  EmployedTypes,
  RegionTypes,
  LibraryCompanies,
  EAvailableTimeTypes,
} from 'enums/base.enum';
import { Checkbox, Form, Typography, Col, Row, Modal, Button, Input } from 'antd';
import { RedoOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { FormWrapper } from 'GlobalStyles';

const { Title } = Typography;

const StyledSettingQuizInfoWrapper = styled.div`
  & .ant-form-item-label > label {
    width: 70px;
  }
`;

const MentorFilterModal = ({ modalState, setModalState }: TModalProps) => {
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);
  const [visible, setVisible] = useState(false);
  //<Form.Item> 태그에서 선택한 값을 form.getFieldValue으로 뽑아내주기 위해
  const [form] = Form.useForm();
  const [seasons, setSeasons] = useState<Array<TSeason>>();
  const [companies, setCompanies] = useState<Array<TCompany>>();
  // const [{data, loading}] = useDataApi<{value: string}>(verifiableCompanyName.bind(null));
  const [categories, setCategories] = useState<Array<{ id: number; name: string }>>();
  const [subcategories, setSubcategories] = useState<Array<{
    id: number;
    name: string;
  }> | null>();

  const [mentoringCompanies, setMentoringCompanies] = useState<Array<{ value: string }>>();

  const getMentoringCompanies = async () => {
    const { data } = await verifiableCompanyName({
      mentoring_field: currentUser?.primary_field,
      mentor: 'mentor',
    });

    if (data) {
      setMentoringCompanies(data);
    }
  };

  const getSeason = async () => {
    const { success, data } = await getCaseSeason();
    if (success && data.length > 0) setSeasons(data);
  };

  const getCompanies = async () => {
    const { success, data } = await getCaseQNACompany();
    if (success) {
      setCompanies(data);
    }
  };

  const getQNACategories = async () => {
    const { success, data } = await getQNACategory();
    if (success) {
      setCategories(data);
    }
  };

  const getQNASubcategories = async () => {
    const { success, data } = await getQNASubcategory();
    if (success) {
      setSubcategories(data);
    }
  };

  //취소 눌렀을 때 => 아무 변화 없이 모달이 꺼짐
  function handleCancel() {
    setModalState({ visible: false, record: '' });
  }

  //확인 눌렀을 때
  function onClickSubmit() {
    //==> 모달이 꺼짐
    setModalState({ visible: false, record: '' });

    //==> 필터링 관련 쿼리 만들기 위해 value 변수에 필터링 상태를 객체로 저장하고 onFinish의 인자로 전달하기
    const value = {
      // jobType: '',
      positionType: '',
      countryType: '',
      companyName: '',
      available_times: '',
      company: '',
      location: '',
      hired_type: '',
      interview: '',
      is_answered: '',
      //tagNames에는 지금가지 선택된 값(string)들이 배열로 들어가있다
      tagNames: [] as any[],
      count: 0,
    };
    //전현직 => 현재는 시니어, 주니어로 구분
    // const jobType = form.getFieldValue('job_type');
    // if (jobType) {
    //   value.jobType = jobType.join('+');
    //   map(jobType, (v: EJobType) => {value.tagNames.push(CJobType[v])});
    //   value.count = 1;
    // }
    //직급
    const positionType = form.getFieldValue('position_type');
    if (positionType) {
      value.positionType = positionType.join('+');
      map(positionType, (v: EPositionType) => {
        value.tagNames.push(CPositionType[v]);
      });
      value.count = 1;
    }
    //회사
    const companyName = form.getFieldValue('company_name');
    if (companyName) {
      value.companyName = companyName.join('+');
      map(companyName, (v: string) => {
        value.tagNames.push(v);
      });
      value.count = 1;
    }
    //학교
    const countryType = form.getFieldValue('country_type');
    if (countryType) {
      value.countryType = countryType.join('+');
      map(countryType, (v: string) => {
        value.tagNames.push(v);
      });
      value.count = 1;
    }
    //(슬랏 열린)시간
    const available_times = form.getFieldValue('available_time');
    if (available_times) {
      value.available_times = available_times.join('+');
      map(available_times, (v: EAvailableTimeTypes) => {
        value.tagNames.push(CAvailableTimeTypes[v]);
      });
      value.count = 1;
    }

    //caseQNA & caseLibrary

    //Form.Item ==> name={'company'}
    const company = form.getFieldValue('company');
    if (company) {
      value.company = company.join('+');
      map(company, (v: string) => {
        value.tagNames.push(v);
      });
      value.count = 1;
    }

    //Form.Item ==> name={'location'}
    const location = form.getFieldValue('location');
    if (location) {
      value.location = location.join('+');
      map(location, (v: string) => {
        value.tagNames.push(v);
      });
      value.count = 1;
    }

    //Form.Item ==> name={'hired_type'}
    const hired_type = form.getFieldValue('hired_type');
    if (hired_type) {
      value.hired_type = hired_type.join('+');
      map(hired_type, (v: string) => {
        value.tagNames.push(v);
      });
      value.count = 1;
    }

    //Form.Item ==> name={'interview'} 기출시기
    const interview = form.getFieldValue('interview');
    if (interview) {
      value.interview = interview.join('+');
      map(interview, (v: string) => {
        value.tagNames.push(v);
      });
      value.count = 1;
    }

    //Form.Item ==> name={'is_answered'} 응답 상태
    //value는 {type.value} ("ansered", "unansered")로 해야할까  {type.text}("응답완료", "미응답") 로 해야할까,,,
    const is_answered = form.getFieldValue('is_answered');
    if (is_answered) {
      value.is_answered = is_answered.join('+');
      map(is_answered, (v: string) => {
        value.tagNames.push(v);
      });
      value.count = 1;
    }

    const QNACategory = form.getFieldValue('QNACategory');
    if (QNACategory) {
      // value.is_answered = is_answered.join('+');
      // map(is_answered, (v: string) => {value.tagNames.push(v)});
      value.count = 1;
    }

    const QNASubcategory = form.getFieldValue('QNASubcategory');
    if (QNASubcategory) {
      // value.is_answered = is_answered.join('+');
      // map(is_answered, (v: string) => {value.tagNames.push(v)});
      value.count = 1;
    }

    // ==> value 변수에 필터링 상태를 객체로 저장하고 onFinish의 인자로 전달하기
    modalState.onFinish && modalState.onFinish(value);
  }

  useEffect(() => {
    setVisible(modalState.visible);
  }, [modalState.visible]);

  useEffect(() => {
    if (modalState.type === 'QNA') {
      getQNACategories();
      getQNASubcategories();
    } else {
      getMentoringCompanies();
    }
  }, [modalState]);

  return (
    <Modal
      visible={visible}
      centered={true}
      maskClosable={true}
      closable={true}
      footer={
        <Row gutter={[8, 8]} justify='space-between'>
          <Col>
            <Button
              onClick={() => {
                form.resetFields();
              }}
            >
              <RedoOutlined />
              초기화
            </Button>
          </Col>
          <Col>
            <Button onClick={handleCancel}>닫기</Button>
            <Button type='primary' onClick={onClickSubmit}>
              적용
            </Button>
          </Col>
        </Row>
      }
      width={540}
      onCancel={handleCancel}
    >
      <Title level={3}>필터</Title>
      <FormWrapper style={{ padding: '0px' }}>
        {modalState.type === 'QNA' && (
          <Form form={form} colon={false} layout='vertical' className='form-normal'>
            <>
              <Form.Item name={'QNACategory'} label='카테고리'>
                <Checkbox.Group className='checkbox-btn'>
                  {!!categories &&
                    categories.map((category) => (
                      <Checkbox key={category.id} value={category.id}>
                        {category.name}
                      </Checkbox>
                    ))}
                </Checkbox.Group>
              </Form.Item>

              <Form.Item name={'QNASubcategory'} label='소주제'>
                <Checkbox.Group className='checkbox-btn'>
                  {!!subcategories &&
                    subcategories.map((subcategory) => (
                      <Checkbox key={subcategory.id} value={subcategory.id}>
                        {subcategory.name}
                      </Checkbox>
                    ))}
                </Checkbox.Group>
              </Form.Item>
            </>
          </Form>
        )}

        {!modalState.type && (
          <Form form={form} colon={false} layout='vertical' className='form-normal'>
            <>
              <Form.Item name={'company_name'} label='회사'>
                <Checkbox.Group className='checkbox-btn'>
                  {map(mentoringCompanies, (v) => (
                    <Checkbox key={get(v, 'value')} value={get(v, 'value')}>
                      {get(v, 'value')}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </Form.Item>
              <Form.Item name={'position_type'} label='직급'>
                <Checkbox.Group className='checkbox-btn'>
                  <Checkbox value={EPositionType.Junior}>{CPositionType[EPositionType.Junior]}</Checkbox>
                  <Checkbox value={EPositionType.Leadership}>
                    {CPositionType[EPositionType.Leadership]}
                  </Checkbox>
                </Checkbox.Group>
              </Form.Item>
              <Form.Item name={'country_type'} label='학교'>
                <Checkbox.Group className='checkbox-btn'>
                  <Checkbox value={ECountryType.KR}>{CCountryType[ECountryType.KR]}</Checkbox>
                  <Checkbox value={ECountryType.US}>{CCountryType[ECountryType.US]}</Checkbox>
                  <Checkbox value={ECountryType.UK}>{CCountryType[ECountryType.UK]}</Checkbox>
                  <Checkbox value={ECountryType.CN}>{CCountryType[ECountryType.CN]}</Checkbox>
                  <Checkbox value={ECountryType.JP}>{CCountryType[ECountryType.JP]}</Checkbox>
                  <Checkbox value={ECountryType.ETC}>{CCountryType[ECountryType.ETC]}</Checkbox>
                </Checkbox.Group>
              </Form.Item>
              <Form.Item name={'available_time'} label='시간'>
                <Checkbox.Group className='checkbox-btn'>
                  <Checkbox value={EAvailableTimeTypes.Today}>
                    {CAvailableTimeTypes[EAvailableTimeTypes.Today]}
                  </Checkbox>
                  <Checkbox value={EAvailableTimeTypes.SevenDays}>
                    {CAvailableTimeTypes[EAvailableTimeTypes.SevenDays]}
                  </Checkbox>
                  <Checkbox value={EAvailableTimeTypes.FourteenDays}>
                    {CAvailableTimeTypes[EAvailableTimeTypes.FourteenDays]}
                  </Checkbox>
                </Checkbox.Group>
              </Form.Item>
              {/* <Form.Item
                  name={'job_type'}
                  label="전,현직"
                >
                  <Checkbox.Group className="checkbox-btn">
                    <Checkbox value={EJobType.Former}>{CJobType[EJobType.Former]}</Checkbox>
                    <Checkbox value={EJobType.Current}>{CJobType[EJobType.Current]}</Checkbox>
                  </Checkbox.Group>
                </Form.Item> */}
            </>
          </Form>
        )}
      </FormWrapper>
    </Modal>
  );
};

MentorFilterModal.defaultProps = {};

export default MentorFilterModal;
